import { ClickableAreaDefinition, ClickableAreasData } from "./ClickableAreasData";
import React, { useCallback, useMemo, useState } from "react";
import { useTreeModifyContext } from "./TreeDataContext";
import { Box, Card, IconButton, ListItem, TextField, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { parseIntWithinLimits } from "./Helpers";

interface ClickableAreaCardProps {
  area: ClickableAreaDefinition;
  areaName: string;
  allClickableAreas: ClickableAreasData;
}

export function ClickableAreaCard(props: ClickableAreaCardProps) {
  const [isModifying, setIsModifying] = useState(false);

  const [newAreaName, setNewAreaName] = useState(props.areaName);
  const [newAreaLeft, setNewAreaLeft] = useState(props.area.left);
  const [newAreaTop, setNewAreaTop] = useState(props.area.top);
  const [newAreaWidth, setNewAreaWidth] = useState(props.area.width);
  const [newAreaHeight, setNewAreaHeight] = useState(props.area.height);

  const { saveChangesAtPath, removeAtPath } = useTreeModifyContext();

  const canBeSaved = useMemo(() => {
    const isAreaValueChanged =
      newAreaLeft !== props.area.left ||
      newAreaTop !== props.area.top ||
      newAreaWidth !== props.area.width ||
      newAreaHeight !== props.area.height;
    if (newAreaName === "") {
      return false;
    }
    if (isAreaValueChanged) {
      return true;
    }
    if (Object.keys(props.allClickableAreas).includes(newAreaName)) {
      return false;
    }
    return true;
  }, [
    props.allClickableAreas,
    props.area.height,
    props.area.left,
    props.area.top,
    props.area.width,
    newAreaHeight,
    newAreaLeft,
    newAreaName,
    newAreaTop,
    newAreaWidth,
  ]);

  const saveChanges = useCallback(() => {
    const newArea = { left: newAreaLeft, top: newAreaTop, width: newAreaWidth, height: newAreaHeight };
    removeAtPath([props.areaName]);
    saveChangesAtPath(newArea, [newAreaName]);
  }, [
    props.areaName,
    newAreaHeight,
    newAreaLeft,
    newAreaName,
    newAreaTop,
    newAreaWidth,
    removeAtPath,
    saveChangesAtPath,
  ]);

  const getSecondaryActions = useCallback(() => {
    if (isModifying) {
      if (canBeSaved) {
        return (
          <IconButton
            color="success"
            aria-label="save"
            onClick={() => {
              saveChanges();
              setIsModifying(false);
            }}
          >
            <CheckIcon />
          </IconButton>
        );
      }
      return (
        <IconButton color="error" aria-label="rename" onClick={() => setIsModifying(false)}>
          <CloseIcon />
        </IconButton>
      );
    }
    return (
      <Box className={"vertical-flex"}>
        <IconButton color="primary" aria-label="rename" onClick={() => setIsModifying(true)}>
          <RenameOutlineIcon />
        </IconButton>
        <IconButton color="error" aria-label="delete" onClick={() => removeAtPath([props.areaName])}>
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  }, [props.areaName, canBeSaved, isModifying, removeAtPath, saveChanges]);

  if (isModifying) {
    return (
      <Card elevation={8} key={props.areaName}>
        <ListItem secondaryAction={getSecondaryActions()}>
          <Box className="card-item">
            <TextField
              variant={"standard"}
              size="small"
              id="new-area-name"
              label="New area name"
              autoFocus={true}
              value={newAreaName}
              onChange={(event) => setNewAreaName(event.target.value)}
            ></TextField>
            <Box className="card-item-settings-2cols">
              <Box className="horizontal-flex">
                <Typography>left:</Typography>
                <TextField
                  variant={"standard"}
                  size="small"
                  id="new-area-left"
                  value={newAreaLeft}
                  onChange={(event) => {
                    setNewAreaLeft(parseIntWithinLimits(event.target.value));
                  }}
                ></TextField>
              </Box>
              <Box className="horizontal-flex">
                <Typography>top: </Typography>
                <TextField
                  variant={"standard"}
                  size="small"
                  id="new-area-top"
                  value={newAreaTop}
                  onChange={(event) => {
                    setNewAreaTop(parseIntWithinLimits(event.target.value));
                  }}
                ></TextField>
              </Box>
              <Box className="horizontal-flex">
                <Typography>width:</Typography>
                <TextField
                  variant={"standard"}
                  size="small"
                  id="new-area-width"
                  value={newAreaWidth}
                  onChange={(event) => {
                    setNewAreaWidth(parseIntWithinLimits(event.target.value));
                  }}
                ></TextField>
              </Box>
              <Box className="horizontal-flex">
                <Typography>height: </Typography>
                <TextField
                  variant={"standard"}
                  size="small"
                  id="new-area-height"
                  value={newAreaHeight}
                  onChange={(event) => {
                    setNewAreaHeight(parseIntWithinLimits(event.target.value));
                  }}
                ></TextField>
              </Box>
            </Box>
          </Box>
        </ListItem>
      </Card>
    );
  }

  return (
    <Card elevation={5} key={props.areaName}>
      <ListItem secondaryAction={getSecondaryActions()}>
        <Box className="card-item">
          <Typography variant="h6">{props.areaName}</Typography>
          <Box className="card-item-settings-2cols">
            <Typography>left: {props.area.left}</Typography>
            <Typography>top: {props.area.top}</Typography>
            <Typography>width: {props.area.width}</Typography>
            <Typography>height: {props.area.height}</Typography>
          </Box>
        </Box>
      </ListItem>
    </Card>
  );
}
